import { useEffect, useState } from 'react'
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, Button, Input } from '@/components/ui'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Resolves when the persona is created; rejection keeps the dialog open. */
  onCreate: (data: { name: string; description?: string }) => Promise<void>
}

/** Minimal blank-create (spec §5 / Q11): name + optional description, then
 *  the caller lands the user in the Workshop to do the real authoring. */
export function BlankPersonaDialog({ open, onOpenChange, onCreate }: Props) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // The dialog is mounted unconditionally by the caller (only `open` toggles),
  // so local state otherwise survives a close/reopen cycle. Reset on every
  // open so a cancelled draft never leaks into the next attempt (mirrors
  // PersonaEditor.tsx's reset-on-open effect).
  useEffect(() => {
    if (open) {
      setName('')
      setDescription('')
    }
  }, [open])

  const handleCreate = async () => {
    if (!name.trim()) return
    setIsCreating(true)
    try {
      await onCreate({ name: name.trim(), description: description.trim() || undefined })
      setName('')
      setDescription('')
      onOpenChange(false)
    } catch {
      // Caller toasts; keep the dialog open so input isn't lost.
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm">
        <ModalHeader>
          <ModalTitle>New blank persona</ModalTitle>
          <ModalDescription>
            Name it and jump straight into the Workshop — identity, knowledge, and behavior are authored there.
          </ModalDescription>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="blank-persona-name" className="text-sm font-medium text-foreground">Name</label>
            <Input id="blank-persona-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Maria the Baker" />
          </div>
          <div className="space-y-2">
            <label htmlFor="blank-persona-desc" className="text-sm font-medium text-foreground">Description (optional)</label>
            <Input id="blank-persona-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One line about who this persona is" />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!name.trim() || isCreating} isLoading={isCreating}>Create</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
